"use client";

import FocusTrap from "focus-trap-react";
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { MobileNavigationButton } from "./items";
import { Breakpoint, FontWeight } from "./types";

// Extend Window interface for TypeScript
declare global {
	interface Window {
		__unifiedHeaderHistoryPatched?: boolean;
	}
}

export interface IMobileNavBarProps {
	readonly siteIdentityImageUrl: string;
	readonly siteIdentityLinkTooltip: string;
	readonly mobileBreakpoint: Breakpoint;
	readonly itemsFontWeight: FontWeight;
	readonly transparentUntilScroll?: boolean;
}
const MobileNavBar: React.FC<PropsWithChildren<IMobileNavBarProps>> = ({
	siteIdentityImageUrl,
	siteIdentityLinkTooltip,
	children,
	mobileBreakpoint,
	itemsFontWeight,
	transparentUntilScroll,
}) => {
	const headerBarRef = useRef<HTMLDivElement>(null);
	const onMobileNavigationChange = useCallback((open: boolean) => {
		document.body.style.overflowY = open ? "hidden" : "";
	}, []);
	const [mobileNavigationOpen, setMobileNavigationOpen] = useState<boolean>(false);
	const onMobileNavigationButtonClick = useCallback(() => {
		onMobileNavigationChange(!mobileNavigationOpen);
		setMobileNavigationOpen(!mobileNavigationOpen);
		if (!mobileNavigationOpen) {
			document.body.style.overflow = "hidden";
		}
	}, [mobileNavigationOpen, setMobileNavigationOpen, onMobileNavigationChange]);
	const onMobileMenuNavigationAreaClickCallback = useCallback(() => {
		onMobileNavigationChange(false);
		setMobileNavigationOpen(false);
		document.body.style.overflow = "";
	}, [onMobileNavigationChange]);
	const preventTouchScrollIfMobileNavigationOpen = useCallback(
		(event: TouchEvent) => {
			if (mobileNavigationOpen) {
				event.preventDefault();
			}
		},
		[mobileNavigationOpen],
	);

	useEffect(() => {
		function handleResize() {
			setMobileNavigationOpen(false);
			onMobileNavigationChange(false);
			document.body.style.overflow = "";
		}
		function handleScroll() {
			if (!transparentUntilScroll) {
				return;
			}
			if (
				window.scrollY > 30 &&
				!headerBarRef.current?.classList.contains("include-background-color")
			) {
				headerBarRef.current?.classList.add("include-background-color");
			} else if (
				window.scrollY <= 30 &&
				headerBarRef.current?.classList.contains("include-background-color")
			) {
				headerBarRef.current?.classList.remove("include-background-color");
			}
		}
		window.addEventListener("resize", handleResize);
		window.addEventListener("scroll", handleScroll);
		window.addEventListener("touchmove", preventTouchScrollIfMobileNavigationOpen);
		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("scroll", handleScroll);
			window.removeEventListener("touchmove", preventTouchScrollIfMobileNavigationOpen);
		};
	}, [
		setMobileNavigationOpen,
		onMobileNavigationChange,
		preventTouchScrollIfMobileNavigationOpen,
		transparentUntilScroll,
		headerBarRef,
	]);
	useEffect(() => {
		// Monkey patch pushState/replaceState to fire a custom event for SPA navigation
		if (typeof window !== "undefined" && !window.__unifiedHeaderHistoryPatched) {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			const origPushState = window.history.pushState;
			// eslint-disable-next-line @typescript-eslint/unbound-method
			const origReplaceState = window.history.replaceState;
			const fireChangeEvent = () => {
				window.dispatchEvent(new Event("epic-header-location-change"));
			};
			window.history.pushState = (...args) => {
				origPushState.apply(window.history, args);
				fireChangeEvent();
			};
			window.history.replaceState = (...args) => {
				origReplaceState.apply(window.history, args);
				fireChangeEvent();
			};
			window.addEventListener("popstate", fireChangeEvent);
			window.__unifiedHeaderHistoryPatched = true;
		}

		// Handler to reset nav on any navigation event
		const handleNavChange = () => {
			setMobileNavigationOpen(false);
			onMobileNavigationChange(false);
			document.body.style.overflow = "";
		};
		window.addEventListener("epic-header-location-change", handleNavChange);
		return () => {
			window.removeEventListener("epic-header-location-change", handleNavChange);
		};
	}, [onMobileNavigationChange]);

	const mobileNavigationButton = (
		<MobileNavigationButton
			mobileNavigationOpen={mobileNavigationOpen}
			onMobileNavigationButtonClick={onMobileNavigationButtonClick}
		/>
	);

	const hiddenAtMobileBreakpoint =
		mobileBreakpoint === Breakpoint.xl
			? "xl:epic-header-hidden"
			: mobileBreakpoint === Breakpoint.lg
				? "lg:epic-header-hidden"
				: "md:epic-header-hidden";

	return (
		<>
			<FocusTrap active={mobileNavigationOpen} className={`z-10 ${hiddenAtMobileBreakpoint}`}>
				<div
					className={`epic-header-fixed epic-header-z-10 ${hiddenAtMobileBreakpoint}`}
					id="navbar_mobile"
				>
					<div
						className={`epic-header-fixed epic-header-top-0 epic-header-bottom-0 epic-header-left-0 epic-header-right-0 epic-header-bg-neutral-600 epic-header-opacity-50 epic-header-z-10 epic-header-cursor-pointer ${hiddenAtMobileBreakpoint} ${
							mobileNavigationOpen ? "block" : "hidden"
						}`}
						onClick={onMobileNavigationButtonClick}
					></div>
					<div
						className={`epic-header-fixed epic-header-w-full epic-header-h-12 epic-header-z-30 epic-header-cursor-pointer ${hiddenAtMobileBreakpoint} ${
							mobileNavigationOpen ? "block" : "hidden"
						}`}
						onClick={onMobileNavigationButtonClick}
					></div>
					<div
						className={`epic-header-fixed epic-header-z-20 epic-header-w-full ${hiddenAtMobileBreakpoint}`}
					>
						<div
							className={`epic-header-flex epic-header-items-center epic-header-justify-between epic-header-h-12 epic-header-px-4 ${transparentUntilScroll ? "transition-colors duration-300 ease-in-out opacity-[.98] [&.include-background-color]:epic-header-bg-[color:var(--epic-nav-bottom-bar-back-color)]" : "epic-header-bg-[color:var(--epic-nav-bottom-bar-back-color)]"}`}
							ref={headerBarRef}
						>
							<a
								id={`navbar_desktop_big_epic_logo_link_mobile`}
								href="/"
								className="epic-header-h-full epic-header-w-fit epic-header-py-[14px] epic-header-pr-2"
								title={siteIdentityLinkTooltip}
							>
								<img
									src={siteIdentityImageUrl}
									alt=""
									className="epic-header-object-contain epic-header-h-full epic-header-aspect-auto"
								/>
							</a>

							{mobileNavigationButton}
						</div>
					</div>
					<nav
						onClick={onMobileMenuNavigationAreaClickCallback}
						className={`epic-header-font-title ${
							itemsFontWeight === FontWeight.ExtraBold
								? "epic-header-font-extrabold"
								: itemsFontWeight === FontWeight.SemiBold
									? "epic-header-font-semibold"
									: "epic-header-font-light"
						} epic-header-fixed ${hiddenAtMobileBreakpoint} epic-header-pt-12 epic-header-w-full epic-header-z-20 epic-header-max-h-screen epic-header-overflow-y-auto ${
							mobileNavigationOpen ? "epic-header-block" : "epic-header-hidden"
						}`}
					>
						{children}
					</nav>
				</div>
			</FocusTrap>
			<div className={`epic-header-block epic-header-mt-[48px] ${hiddenAtMobileBreakpoint}`}></div>
		</>
	);
};
export default MobileNavBar;

